import { Link } from 'react-router';
import Logo from '@/components/Logo';
import { useLogout } from '@/lib/session';
import { panierMemorise, useOrders } from '@/lib/orders';
import type { MeResponse } from '@/lib/api';
import { formatMoney } from '@/lib/utils';

/** Plan §5.2 — accueil : nouvelle commande, mes commandes du jour, mon total. */
export default function Home({ session }: { session: MeResponse }) {
  const logout = useLogout();
  const { data } = useOrders();
  const brouillonMemorise = panierMemorise();

  const commandes = data?.orders ?? [];
  const brouillonEnCours = commandes.find(
    (o) => o.id === brouillonMemorise && o.status === 'DRAFT',
  );

  return (
    <main className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <Logo className="mb-3 h-12" />
          <h1 className="text-3xl font-bold">{session.user.fullName}</h1>
          <p className="text-muted-foreground">
            {session.user.username} ·{' '}
            {session.user.role === 'ADMIN' ? 'Administrateur' : 'Représentant'}
          </p>
        </div>
        <button
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          className="border-input h-12 rounded-lg border px-5 font-medium disabled:opacity-50"
        >
          Déconnexion
        </button>
      </header>

      {/* Reprise du panier abandonné : un iPad qui se verrouille ou une PWA
          déchargée par Safari ne doit pas faire perdre une commande en cours. */}
      {brouillonEnCours && (
        <Link
          to={`/order/${brouillonEnCours.id}`}
          className="block rounded-xl border border-amber-500/40 bg-amber-500/10 p-4"
        >
          <p className="font-medium">Commande en cours à reprendre</p>
          <p className="text-muted-foreground text-sm">
            {brouillonEnCours.customerName} · {formatMoney(brouillonEnCours.total)}
          </p>
        </Link>
      )}

      <div className="flex flex-wrap gap-3">
        <Link
          to="/order/new"
          className="bg-primary text-primary-foreground inline-flex h-20 flex-1 items-center justify-center rounded-xl px-8 text-xl font-semibold"
        >
          Nouvelle commande
        </Link>
        <Link
          to="/catalogue"
          className="border-input inline-flex h-20 items-center rounded-xl border px-8 text-lg font-semibold"
        >
          Catalogue
        </Link>
        {session.user.role === 'ADMIN' && (
          <Link
            to="/admin"
            className="border-input inline-flex h-20 items-center rounded-xl border px-8 text-lg font-semibold"
          >
            Administration
          </Link>
        )}
      </div>

      <section className="bg-card rounded-xl border p-6">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="font-semibold">Mes commandes soumises</h2>
          <p className="text-2xl font-bold tabular-nums">{formatMoney(data?.submittedTotal ?? '0')}</p>
        </div>

        {commandes.length === 0 ? (
          <p className="text-muted-foreground text-sm">Aucune commande pour l'instant.</p>
        ) : (
          <ul className="divide-y">
            {commandes.map((o) => (
              <li key={o.id}>
                <Link to={`/order/${o.id}`} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="font-medium">{o.customerName}</p>
                    <p className="text-muted-foreground font-mono text-xs">
                      {o.number ?? 'brouillon'} · {o.custNum}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-semibold tabular-nums">{formatMoney(o.total)}</p>
                    {o.status !== 'SUBMITTED' && (
                      <p className="text-muted-foreground text-xs">
                        {o.status === 'DRAFT' ? 'brouillon' : 'annulée'}
                      </p>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-3 font-semibold">Mes codes de ligne</h2>
        {session.lines.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Aucune ligne assignée — contactez un administrateur.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {session.lines.map((l) => (
              <li
                key={l.id}
                className="bg-secondary text-secondary-foreground rounded-md px-3 py-1.5 text-sm font-medium"
              >
                {l.code} — {l.name}
              </li>
            ))}
          </ul>
        )}
        <p className="text-muted-foreground mt-4 text-xs">
          Vous ne pouvez consulter et vendre que les produits de ces lignes. Cette règle est
          appliquée par le serveur, pas par cet écran.
        </p>
      </section>
    </main>
  );
}
